// // src/hooks/api/useApiQuery.ts

// import {
//   useQuery,
//   useInfiniteQuery,
//   useSuspenseQuery,
//   useSuspenseInfiniteQuery,
//   type QueryKey,
// } from "@tanstack/react-query";
// import { httpService } from "@/core/api/services/http.service";
// import type { PaginatedResponse, ListParams, ID } from "@/types/global";
// import type {
//   QueryOptions,
//   ListQueryOptions,
//   InfiniteQueryOptions,
// } from "./types";
// import { type ApiError, parseApiError, type ParsedError } from "./errors";

// // ================================
// // Result Types
// // ================================

// interface BaseQueryResult<TData> {
//   data: TData | undefined;
//   error: ApiError | null;
//   parsedError: ParsedError | null;
//   isLoading: boolean;
//   isPending: boolean;
//   isError: boolean;
//   isSuccess: boolean;
//   isFetching: boolean;
//   isRefetching: boolean;
//   refetch: () => void;
// }

// interface ListQueryResult<TData> extends BaseQueryResult<
//   PaginatedResponse<TData>
// > {
//   items: TData[];
//   meta: PaginatedResponse<TData>["data"]["meta"] | undefined;
//   total: number;
//   isEmpty: boolean;
// }

// interface InfiniteQueryResult<TData> {
//   items: TData[];
//   total: number;
//   error: ApiError | null;
//   parsedError: ParsedError | null;
//   isLoading: boolean;
//   isPending: boolean;
//   isError: boolean;
//   isSuccess: boolean;
//   isFetching: boolean;
//   isFetchingNextPage: boolean;
//   isFetchingPreviousPage: boolean;
//   hasNextPage: boolean;
//   hasPreviousPage: boolean;
//   fetchNextPage: () => void;
//   fetchPreviousPage: () => void;
//   refetch: () => void;
// }

// // ================================
// // Generic Query Hook
// // ================================

// interface UseApiQueryParams<TData> extends QueryOptions<TData> {
//   queryKey: QueryKey;
//   url: string;
// }

// /**
//  * Generic GET request hook
//  */
// export const useApiQuery = <TData = unknown>({
//   queryKey,
//   url,
//   axiosConfig,
//   ...options
// }: UseApiQueryParams<TData>): BaseQueryResult<TData> => {
//   const query = useQuery<TData, ApiError, TData, QueryKey>({
//     queryKey,
//     queryFn: ({ signal }) =>
//       httpService.get<TData>(url, { ...axiosConfig, signal }),
//     ...options,
//   });

//   return {
//     ...query,
//     parsedError: query.error ? parseApiError(query.error) : null,
//   };
// };

// // ================================
// // Single Item Query
// // ================================

// interface UseApiGetParams<TData> extends QueryOptions<TData> {
//   queryKey: QueryKey;
//   url: string;
//   id?: ID;
// }

// /**
//  * Fetch single item by ID
//  */
// export const useApiGet = <TData = unknown>({
//   queryKey,
//   url,
//   id,
//   axiosConfig,
//   enabled = true,
//   ...options
// }: UseApiGetParams<TData>): BaseQueryResult<TData> => {
//   const query = useQuery<TData, ApiError, TData, QueryKey>({
//     queryKey,
//     queryFn: ({ signal }) =>
//       httpService.get<TData>(url, { ...axiosConfig, signal }),
//     // enabled: enabled && id != null && id !== "",
//     refetchOnMount: true,
//     ...options,
//   });

//   return {
//     ...query,
//     parsedError: query.error ? parseApiError(query.error) : null,
//   };
// };

// // ================================
// // Paginated List Query
// // ================================

// interface UseApiListParams<TData> extends ListQueryOptions<TData> {
//   queryKey: QueryKey;
//   url: string;
// }

// /**
//  * Fetch paginated list
//  */
// export const useApiList = <TData = unknown>({
//   queryKey,
//   url,
//   params,
//   axiosConfig,
//   ...options
// }: UseApiListParams<TData>): ListQueryResult<TData> => {
//   const query = useQuery<
//     PaginatedResponse<TData>,
//     ApiError,
//     PaginatedResponse<TData>,
//     QueryKey
//   >({
//     queryKey,
//     queryFn: ({ signal }) =>
//       httpService.get<PaginatedResponse<TData>>(url, {
//         ...axiosConfig,
//         params: { ...axiosConfig?.params, ...params },
//         signal,
//       }),
//     ...options,
//   });

//   const items = query.data?.data?.data ?? [];
//   const meta = query.data?.data?.meta;

//   return {
//     ...query,
//     parsedError: query.error ? parseApiError(query.error) : null,
//     items,
//     meta,
//     total: meta?.totalItems ?? 0,
//     isEmpty: query.isSuccess && items.length === 0,
//   };
// };

// // ================================
// // Infinite Query (SIMPLIFIED)
// // ================================

// interface UseApiInfiniteParams extends InfiniteQueryOptions {
//   queryKey: QueryKey;
//   url: string;
//   params?: Omit<ListParams, "page">;
//   limit?: number;
// }

// /**
//  * Infinite scroll pagination
//  */
// export const useApiInfinite = <TData = unknown>({
//   queryKey,
//   url,
//   params,
//   limit = 20,
//   axiosConfig,
//   enabled = true,
//   staleTime,
//   gcTime,
//   refetchOnWindowFocus,
//   refetchOnMount,
//   refetchOnReconnect,
//   retry,
//   retryDelay,
// }: UseApiInfiniteParams): InfiniteQueryResult<TData> => {
//   const query = useInfiniteQuery({
//     queryKey,
//     queryFn: async ({ pageParam, signal }) => {
//       const response = await httpService.get<PaginatedResponse<TData>>(url, {
//         ...axiosConfig,
//         params: { ...axiosConfig?.params, ...params, page: pageParam, limit },
//         signal,
//       });
//       return response;
//     },
//     initialPageParam: 1,
//     getNextPageParam: (lastPage: PaginatedResponse<TData>) =>
//       lastPage.data.meta.hasNextPage
//         ? lastPage.data.meta.currentPage + 1
//         : undefined,
//     getPreviousPageParam: (firstPage: PaginatedResponse<TData>) =>
//       firstPage.data.meta.hasPreviousPage
//         ? firstPage.data.meta.currentPage - 1
//         : undefined,
//     enabled,
//     staleTime,
//     gcTime,
//     refetchOnWindowFocus,
//     refetchOnMount,
//     refetchOnReconnect,
//     retry,
//     retryDelay,
//   });

//   // Extract items from pages
//   const pages = query.data?.pages ?? [];
//   const items = pages.flatMap((page) => page.data.data);
//   const total = pages[0]?.data.meta.totalItems ?? 0;

//   return {
//     items,
//     total,
//     error: query.error as ApiError | null,
//     parsedError: query.error ? parseApiError(query.error as ApiError) : null,
//     isLoading: query.isLoading,
//     isPending: query.isPending,
//     isError: query.isError,
//     isSuccess: query.isSuccess,
//     isFetching: query.isFetching,
//     isFetchingNextPage: query.isFetchingNextPage,
//     isFetchingPreviousPage: query.isFetchingPreviousPage,
//     hasNextPage: query.hasNextPage,
//     hasPreviousPage: query.hasPreviousPage,
//     fetchNextPage: query.fetchNextPage,
//     fetchPreviousPage: query.fetchPreviousPage,
//     refetch: query.refetch,
//   };
// };

// // ================================
// // Suspense Query
// // ================================

// /**
//  * Suspense-enabled query
//  */
// export const useApiSuspenseQuery = <TData = unknown>({
//   queryKey,
//   url,
//   axiosConfig,
//   ...options
// }: Omit<UseApiQueryParams<TData>, "enabled">) => {
//   return useSuspenseQuery<TData, ApiError, TData, QueryKey>({
//     queryKey,
//     queryFn: ({ signal }) =>
//       httpService.get<TData>(url, { ...axiosConfig, signal }),
//     ...options,
//   });
// };

// // ================================
// // Suspense Infinite Query (SIMPLIFIED)
// // ================================

// interface UseApiSuspenseInfiniteParams {
//   queryKey: QueryKey;
//   url: string;
//   params?: Omit<ListParams, "page">;
//   limit?: number;
//   axiosConfig?: import("axios").AxiosRequestConfig;
//   staleTime?: number;
//   gcTime?: number;
// }

// /**
//  * Suspense-enabled infinite query
//  */
// export const useApiSuspenseInfinite = <TData = unknown>({
//   queryKey,
//   url,
//   params,
//   limit = 20,
//   axiosConfig,
//   staleTime,
//   gcTime,
// }: UseApiSuspenseInfiniteParams) => {
//   const query = useSuspenseInfiniteQuery({
//     queryKey,
//     queryFn: async ({ pageParam, signal }) => {
//       const response = await httpService.get<PaginatedResponse<TData>>(url, {
//         ...axiosConfig,
//         params: { ...axiosConfig?.params, ...params, page: pageParam, limit },
//         signal,
//       });
//       return response;
//     },
//     initialPageParam: 1,
//     getNextPageParam: (lastPage: PaginatedResponse<TData>) =>
//       lastPage.data.meta.hasNextPage
//         ? lastPage.data.meta.currentPage + 1
//         : undefined,
//     getPreviousPageParam: (firstPage: PaginatedResponse<TData>) =>
//       firstPage.data.meta.hasPreviousPage
//         ? firstPage.data.meta.currentPage - 1
//         : undefined,
//     staleTime,
//     gcTime,
//   });

//   const pages = query.data?.pages ?? [];

//   return {
//     ...query,
//     items: pages.flatMap((page) => page.data.data),
//     total: pages[0]?.data.meta.totalItems ?? 0,
//   };
// };

// src/hooks/api/useApiQuery.ts
// ═══════════════════════════════════════════════════════════════════════════════
// ENHANCED API QUERY HOOKS
// ═══════════════════════════════════════════════════════════════════════════════

import {
  useQuery,
  useInfiniteQuery,
  useSuspenseQuery,
  useSuspenseInfiniteQuery,
  type QueryKey,
} from "@tanstack/react-query";
import { httpService } from "@/core/api/services/http.service";
import type { PaginatedResponse, ListParams, ID } from "@/types/global";
import type {
  QueryOptions,
  ListQueryOptions,
  InfiniteQueryOptions,
} from "./types";
import { type ApiError, type ParsedError, parseApiError } from "./errors";

// ═══════════════════════════════════════════════════════════════════════════════
// RESULT TYPES
// ═══════════════════════════════════════════════════════════════════════════════

interface BaseQueryResult<TData> {
  // Data
  data: TData | undefined;

  // Loading states
  isLoading: boolean;
  isPending: boolean;
  isFetching: boolean;
  isRefetching: boolean;

  // Status
  isError: boolean;
  isSuccess: boolean;

  // Error information
  error: ApiError | null;
  parsedError: ParsedError | null;
  errorMessage: string | null;
  isRetryable: boolean;

  // Error flags
  isNetworkError: boolean;
  isTimeoutError: boolean;
  isAuthError: boolean;
  isAuthorizationError: boolean;
  isValidationError: boolean;
  isNotFoundError: boolean;
  isRateLimitError: boolean;
  isServerError: boolean;

  // Actions
  refetch: () => void;
}

interface ListQueryResult<TData> extends BaseQueryResult<
  PaginatedResponse<TData>
> {
  items: TData[];
  meta: PaginatedResponse<TData>["meta"] | undefined;
  total: number;
  isEmpty: boolean;
}

interface InfiniteQueryResult<TData> {
  items: TData[];
  total: number;

  // Error
  error: ApiError | null;
  parsedError: ParsedError | null;
  errorMessage: string | null;
  isRetryable: boolean;
  isNetworkError: boolean;
  isTimeoutError: boolean;
  isAuthError: boolean;
  isAuthorizationError: boolean;
  isValidationError: boolean;
  isNotFoundError: boolean;
  isRateLimitError: boolean;
  isServerError: boolean;

  // Loading
  isLoading: boolean;
  isPending: boolean;
  isError: boolean;
  isSuccess: boolean;
  isFetching: boolean;
  isFetchingNextPage: boolean;
  isFetchingPreviousPage: boolean;

  // Pagination
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  fetchNextPage: () => void;
  fetchPreviousPage: () => void;
  refetch: () => void;
}

// ═══════════════════════════════════════════════════════════════════════════════
// HELPER: Create Error Result
// ═══════════════════════════════════════════════════════════════════════════════

function createErrorResult(error: ApiError | null) {
  const parsedError = error ? parseApiError(error) : null;

  return {
    error,
    parsedError,
    errorMessage: parsedError?.message ?? null,
    isRetryable: parsedError?.isRetryable ?? false,
    isNetworkError: parsedError?.isNetworkError ?? false,
    isTimeoutError: parsedError?.isTimeoutError ?? false,
    isAuthError: parsedError?.isAuthError ?? false,
    isAuthorizationError: parsedError?.isAuthorizationError ?? false,
    isValidationError: parsedError?.isValidationError ?? false,
    isNotFoundError: parsedError?.isNotFoundError ?? false,
    isRateLimitError: parsedError?.isRateLimitError ?? false,
    isServerError: parsedError?.isServerError ?? false,
  };
}

// ═══════════════════════════════════════════════════════════════════════════════
// GENERIC QUERY HOOK
// ═══════════════════════════════════════════════════════════════════════════════

interface UseApiQueryParams<TData> extends QueryOptions<TData> {
  queryKey: QueryKey;
  url: string;
}

/**
 * Generic GET request hook
 */
export const useApiQuery = <TData = unknown>({
  queryKey,
  url,
  axiosConfig,
  ...options
}: UseApiQueryParams<TData>): BaseQueryResult<TData> => {
  const query = useQuery<TData, ApiError, TData, QueryKey>({
    queryKey,
    queryFn: ({ signal }) =>
      httpService.get<TData>(url, { ...axiosConfig, signal }),
    ...options,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
    ...createErrorResult(query.error ?? null),
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// SINGLE ITEM QUERY
// ═══════════════════════════════════════════════════════════════════════════════

interface UseApiGetParams<TData> extends QueryOptions<TData> {
  queryKey: QueryKey;
  url: string;
  id?: ID;
}

/**
 * Fetch single item by ID
 */
export const useApiGet = <TData = unknown>({
  queryKey,
  url,
  id,
  axiosConfig,
  enabled = true,
  ...options
}: UseApiGetParams<TData>): BaseQueryResult<TData> => {
  const query = useQuery<TData, ApiError, TData, QueryKey>({
    queryKey,
    queryFn: ({ signal }) =>
      httpService.get<TData>(url, { ...axiosConfig, signal }),
    enabled: enabled && (id == null || id !== ""),
    refetchOnMount: true,
    ...options,
  });

  return {
    data: query.data,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
    ...createErrorResult(query.error ?? null),
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// PAGINATED LIST QUERY
// ═══════════════════════════════════════════════════════════════════════════════

interface UseApiListParams<TData> extends ListQueryOptions<TData> {
  queryKey: QueryKey;
  url: string;
}

/**
 * Fetch paginated list
 */
export const useApiList = <TData = unknown>({
  queryKey,
  url,
  params,
  axiosConfig,
  ...options
}: UseApiListParams<TData>): ListQueryResult<TData> => {
  const query = useQuery<
    PaginatedResponse<TData>,
    ApiError,
    PaginatedResponse<TData>,
    QueryKey
  >({
    queryKey,
    queryFn: ({ signal }) =>
      httpService.get<PaginatedResponse<TData>>(url, {
        ...axiosConfig,
        params: { ...axiosConfig?.params, ...params },
        signal,
      }),
    ...options,
  });

  const items = query.data?.data ?? [];
  const meta = query.data?.meta;

  return {
    data: query.data,
    items,
    meta,
    total: meta?.totalItems ?? 0,
    isEmpty: query.isSuccess && items.length === 0,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isFetching: query.isFetching,
    isRefetching: query.isRefetching,
    isError: query.isError,
    isSuccess: query.isSuccess,
    refetch: query.refetch,
    ...createErrorResult(query.error ?? null),
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// INFINITE QUERY
// ═══════════════════════════════════════════════════════════════════════════════

interface UseApiInfiniteParams extends InfiniteQueryOptions {
  queryKey: QueryKey;
  url: string;
  params?: Omit<ListParams, "page">;
  limit?: number;
}

/**
 * Infinite scroll pagination
 */
export const useApiInfinite = <TData = unknown>({
  queryKey,
  url,
  params,
  limit = 20,
  axiosConfig,
  enabled = true,
  staleTime,
  gcTime,
  refetchOnWindowFocus,
  refetchOnMount,
  refetchOnReconnect,
  retry,
  retryDelay,
}: UseApiInfiniteParams): InfiniteQueryResult<TData> => {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const response = await httpService.get<PaginatedResponse<TData>>(url, {
        ...axiosConfig,
        params: { ...axiosConfig?.params, ...params, page: pageParam, limit },
        signal,
      });
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<TData>) =>
      lastPage.meta.hasNextPage ? lastPage.meta.currentPage + 1 : undefined,
    getPreviousPageParam: (firstPage: PaginatedResponse<TData>) =>
      firstPage.meta.hasPreviousPage
        ? firstPage.meta.currentPage - 1
        : undefined,
    enabled,
    staleTime,
    gcTime,
    refetchOnWindowFocus,
    refetchOnMount,
    refetchOnReconnect,
    retry,
    retryDelay,
  });

  const pages = query.data?.pages ?? [];
  const items = pages.flatMap((page) => page.data);
  const total = pages[0]?.meta.totalItems ?? 0;
  const errorResult = createErrorResult(query.error as ApiError | null);

  return {
    items,
    total,
    isLoading: query.isLoading,
    isPending: query.isPending,
    isError: query.isError,
    isSuccess: query.isSuccess,
    isFetching: query.isFetching,
    isFetchingNextPage: query.isFetchingNextPage,
    isFetchingPreviousPage: query.isFetchingPreviousPage,
    hasNextPage: query.hasNextPage,
    hasPreviousPage: query.hasPreviousPage,
    fetchNextPage: query.fetchNextPage,
    fetchPreviousPage: query.fetchPreviousPage,
    refetch: query.refetch,
    ...errorResult,
  };
};

// ═══════════════════════════════════════════════════════════════════════════════
// SUSPENSE QUERIES
// ═══════════════════════════════════════════════════════════════════════════════

export const useApiSuspenseQuery = <TData = unknown>({
  queryKey,
  url,
  axiosConfig,
  ...options
}: Omit<UseApiQueryParams<TData>, "enabled">) => {
  return useSuspenseQuery<TData, ApiError, TData, QueryKey>({
    queryKey,
    queryFn: ({ signal }) =>
      httpService.get<TData>(url, { ...axiosConfig, signal }),
    ...options,
  });
};

interface UseApiSuspenseInfiniteParams {
  queryKey: QueryKey;
  url: string;
  params?: Omit<ListParams, "page">;
  limit?: number;
  axiosConfig?: import("axios").AxiosRequestConfig;
  staleTime?: number;
  gcTime?: number;
}

export const useApiSuspenseInfinite = <TData = unknown>({
  queryKey,
  url,
  params,
  limit = 20,
  axiosConfig,
  staleTime,
  gcTime,
}: UseApiSuspenseInfiniteParams) => {
  const query = useSuspenseInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam, signal }) => {
      const response = await httpService.get<PaginatedResponse<TData>>(url, {
        ...axiosConfig,
        params: { ...axiosConfig?.params, ...params, page: pageParam, limit },
        signal,
      });
      return response;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage: PaginatedResponse<TData>) =>
      lastPage.meta.hasNextPage ? lastPage.meta.currentPage + 1 : undefined,
    getPreviousPageParam: (firstPage: PaginatedResponse<TData>) =>
      firstPage.meta.hasPreviousPage
        ? firstPage.meta.currentPage - 1
        : undefined,
    staleTime,
    gcTime,
  });

  const pages = query.data?.pages ?? [];

  return {
    ...query,
    items: pages.flatMap((page) => page?.data),
    total: pages[0]?.meta.totalItems ?? 0,
  };
};
